import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { History, Sparkles, X } from 'lucide-react';
import { werseeAi, type StudioJob } from '../ai/client';
import { AnimatePresence, motion, LayoutGroup, useReducedMotion } from 'motion/react';
import { CreateGallery } from '../components/store/CreateGallery';
import { ProductStudio } from '../components/store/ProductStudio';
import { SEO } from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { clearStudioCollection, readStudioCollection, type StudioCollection } from '../lib/productStudioCollection';

export function Create() {
  const navigate = useNavigate(); const { user } = useAuth(); const reduced = useReducedMotion();
  const location = useLocation(); const [params, setParams] = useSearchParams();
  const projectId = params.get('project') || undefined;
  const [idea, setIdea] = useState<{ prompt: string; files: File[] } | null>(() => typeof location.state?.buddyPrompt === 'string' ? { prompt: location.state.buddyPrompt, files: [] } : null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [projects, setProjects] = useState<StudioJob[]>([]);
  const [historyError, setHistoryError] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saved, setSaved] = useState<StudioCollection | null>(null);
  const [resuming, setResuming] = useState(false);
  useEffect(() => { setSaved(user ? readStudioCollection(user.id) : null); }, [user?.id]);
  useEffect(() => {
    if (!historyOpen || !user) return;
    let active = true; setHistoryLoading(true); setHistoryError('');
    werseeAi.listStudioProjects().then(data => { if (active) setProjects(data.projects); }).catch(() => { if (active) setHistoryError('Geschiedenis kon niet laden. Open opnieuw om het te proberen.'); }).finally(() => { if (active) setHistoryLoading(false); });
    return () => { active = false; };
  }, [historyOpen, user?.id]);
  useEffect(() => {
    if (!historyOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setHistoryOpen(false); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [historyOpen]);
  const closeStudio = () => { setIdea(null); navigate('/create', { replace: true, state: null }); setResuming(false); setSaved(user ? readStudioCollection(user.id) : null); };
  const start = (prompt: string, files: File[]) => {
    if (!user) { navigate('/signin?returnUrl=/create'); return; }
    clearStudioCollection(user.id); setParams({}); setSaved(null); setIdea({ prompt, files });
  };
  return <div className={`ws-discover ws-create-page${user ? '' : ' ws-buddy-guest'}`}>
    <SEO title="Buddy Studio · Create on Wersee" description="Turn an idea into your next product." url="/create" />
    <header className="ws-buddy-toolbar">
      <span>Wersee Buddy</span>
      <div>
        <button type="button" className="ws-buddy-create" onClick={closeStudio}><Sparkles size={15} />Create</button>
        <button type="button" aria-label="Open Buddy history" aria-expanded={historyOpen} className="ws-buddy-history" onClick={() => setHistoryOpen(open => !open)}><History size={18} /></button>
      </div>
    </header>
    {historyOpen && <aside className="ws-buddy-history-panel" aria-label="Buddy product history">
      <div><h2>History</h2><button type="button" aria-label="Close history" onClick={() => setHistoryOpen(false)}><X size={18} /></button></div>
      {!user && <p>Log in om je projecten te bekijken.</p>}
      {historyLoading && <p role="status">Projecten laden…</p>}
      {historyError && <p role="alert">{historyError}</p>}
      {user && !historyLoading && !historyError && !projects.length && <p>Je eerste idee begint hier.</p>}
      {projects.map(project => <button type="button" key={project.id} onClick={() => { setIdea(null); setResuming(false); setParams({ project: project.id }); setHistoryOpen(false); }}><strong>{project.title}</strong><small>{project.stage} · {new Date(project.updatedAt).toLocaleDateString()}</small></button>)}
    </aside>}
    <LayoutGroup><AnimatePresence mode="wait">{idea || projectId || resuming && saved
      ? <ProductStudio key={projectId || 'studio'} initialProjectId={projectId} initialPrompt={idea?.prompt} initialFiles={idea?.files} initialCollection={resuming && saved ? saved : undefined} onClose={closeStudio} />
      : <motion.div key="gallery" exit={{ opacity: 0, y: reduced ? 0 : -24, scale: reduced ? 1 : .985 }} transition={{ duration: .3 }}>
        {saved && <div className="ws-create-resume"><span>Je product is bewaard.</span><button type="button" onClick={() => saved.projectId ? setParams({ project: saved.projectId }) : setResuming(true)}>Verder werken</button></div>}
        <CreateGallery onSelect={(type, digitalType) => navigate(user ? `/create/${type}${digitalType ? `?digitalType=${digitalType}` : ''}` : '/signin?returnUrl=/create')} onPrompt={start} />
      </motion.div>}
    </AnimatePresence></LayoutGroup>
  </div>;
}
